import nBossConnection from 'modules/base/service/nBossConnection';
import TableFilterListDto from 'modules/base/dto/table/TableFilterListDto';
import { useParams, withRouter } from 'react-router-dom';
import FuseLoading from '@fuse/core/FuseLoading/FuseLoading';
import _ from '@lodash';
import { useEffect, useMemo, useState, useRef } from 'react';
import { Checkbox, Button, ThemeProvider, Dialog, DialogContent, DialogActions, DialogTitle, InputAdornment, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, TextField, Paper, Tooltip, IconButton, Tabs, Tab } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import LoadIcon from '@material-ui/icons/SystemUpdateAltOutlined';
import LinkIcon from '@material-ui/icons/Link';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import SearchIcon from '@material-ui/icons/Search';
import EditFormTextField from 'modules/ui/editform/fields/EditFormTextField';
import ValueStyleConfigFields from 'app/admin/common/component/ValueStyleConfigFields';
import defaultLabelStyle from 'app/admin/common/component/DefaultLabelStyle';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import ConfirmDialog from 'modules/ui/component/ConfirmDialog';
import { BsClockHistory } from 'react-icons/bs';
import { useSelector, useDispatch } from 'react-redux';
import { showMessage } from 'app/store/fuse/messageSlice';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';
import { useWhatIf } from './WhatIfProvider';
import { generateId } from './WhatIfUtil';
import WhatIfExperimentTableService from '../service/WhatIfExperimentTableService';
import WhatIfExperimentEditDto from '../dto/WhatIfExperimentEditDto';
import WhatIfExperimentEditService from '../service/WhatIfExperimentEditService';
import ExperimentCompare from './compare/ExperimentCompare';
import CompareViewComponent from './compare/CompareViewComponent';

const defaultStyle = {
	variableStyle: defaultLabelStyle(false),
	componentStyle: defaultLabelStyle(true)
};

const getEntityId = entity => (_.isObject(entity) ? entity.id : entity);

export default withRouter(function WhatIfExperiment() {
	const [tabValue, setTabValue] = useState(0);
	const { id } = useParams();
	const { t } = useTranslation(['whatIf', 'tableBrowser']);
	const { data, setExperiment, setCompare, setViewMessage } = useWhatIf();
	const [sessionId, setSessionId] = useState(null);
	const [loading, setLoading] = useState(false);
	const [showDialog, setShowDialog] = useState(false);
	const [viewMode, setViewMode] = useState('LIST');
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
	const [experimentList, setStateList] = useState([]);
	const [experimentSearch, setExperimentSearch] = useState('');
	const [indeterminate, setIndeterminate] = useState(false);
	const [selected, setSelected] = useState([]);
	const styleRef = useRef({ ...defaultStyle });
	const mainTheme = useSelector(selectMainTheme);
	const dispatch = useDispatch();
	const currentExperiment = data ? data.experiment : null;
	const currentAspectId = currentExperiment ? getEntityId(currentExperiment.aspect) : null;
	const currentTransformerId = currentExperiment ? getEntityId(currentExperiment.transformer) : null;
	const canExport = id !== 'new' && currentAspectId != null;
	const canSaveExperiment = currentExperiment != null;
	const filteredExperimentList = useMemo(() => {
		const searchValue = experimentSearch.trim().toLowerCase();
		if (!searchValue) return experimentList;

		return experimentList.filter(experiment => [
			experiment.name,
			experiment.creator ? experiment.creator.name : null
		].filter(Boolean).join(' ').toLowerCase().includes(searchValue));
	}, [experimentList, experimentSearch]);

	useEffect(() => {
		if (showDialog) {
			setLoading(true);
			if (sessionId === null) {
				const newId = generateId();
				const connectFilter = [];
				console.log(data);
				if (currentAspectId != null) {
					connectFilter.push({
						propertyName: 'aspect',
						propertyType: 'Long',
						type: 'EQ',
						value: currentAspectId
					});
				} else if (currentTransformerId != null) {
					connectFilter.push({
						propertyName: 'transformer',
						propertyType: 'Long',
						type: 'EQ',
						value: currentTransformerId
					});
				}
				WhatIfExperimentTableService.getInstance(
					newId,
					true,
					new TableFilterListDto().setup({
						list: [
							{
								propertyName: 'whatIf',
								propertyType: 'Long',
								type: 'EQ',
								value: Number.parseInt(id, 10)
							},
							...connectFilter
						],
						activeCount: 2
					})
				).then(resp => {
					setSessionId(newId);
				});
			} else {
				getList();
			}
		}
	}, [showDialog]);

	useEffect(() => {
		if (sessionId) getList();
	}, [sessionId]);

	const getList = () => {
		WhatIfExperimentTableService.getList(id, sessionId, 0, 0).then(result => {
			setStateList(result.data || []);
			setLoading(false);
		});
	};

	const schema = yup.object().shape({
		name: yup.string().nullable().required(t('validation:FIELD_REQUIRED'))
	});

	const methods = useForm({
		defaultValues: { name: null },
		mode: 'onChange',
		resolver: yupResolver(schema)
	});

	const handleSave = dto => {
		if (!canSaveExperiment) {
			dispatch(showMessage({ message: 'No aspect or transformer selected' }));
			return;
		}

		const result = new WhatIfExperimentEditDto().setup({
			...currentExperiment,
			name: dto.name,
			whatIf: id
		});
		WhatIfExperimentEditService.save(result).then(() => {
			getList();
			setViewMode('LIST');
		});
	};

	const handleDelete = () => {
		WhatIfExperimentTableService.deleteById(sessionId, showDeleteConfirm).then(() => {
			getList();
			setShowDeleteConfirm(null);
			setLoading(true);
		});
	};

	const loadExperiment = item => {
		setExperiment({
			id: item.id,
			name: item.name,
			aspect: item.aspect ? item.aspect.id : null,
			transformer: item.transformer ? item.transformer.id : null,
			whatIf: item.whatIf,
			validity: _.isBoolean(item.validity) ? item.validity : true,
			attributes: item.attributes
		});
		setViewMessage(item && item.name ? `You are currently viewing ${item.name} experiment.` : null);
		setShowDialog(false);
	};

	const copyExperimentLink = async item => {
		const link = window.location.origin + '/#/what-if/edit/' + id + '/E' + item.id;

		try {
			await navigator.clipboard.writeText(link);
			dispatch(showMessage({ message: 'Experiment link copied to clipboard' }));
		} catch (error) {
			dispatch(showMessage({ message: 'Failed to copy experiment link' }));
		}
	};

	const handleSelectedChange = experimentId => {
		if (selected.indexOf(experimentId) > -1) setSelected(selected.filter(d => d !== experimentId));
		else setSelected([...selected, experimentId]);
	};

	const handleSelectAllClick = () => {
		if (selected.length > 0 && selected.length === experimentList.length) {
			setSelected([]);
		} else {
			setSelected(experimentList.map(d => d.id));
		}
	};

	const setCompareOnScreen = () => {
		if (selected.length === 1) {
			setCompare(experimentList.find(e => e.id === selected[0]));
			setShowDialog(false);
		}
	};

	return (
		<>
			<Button
				className="whitespace-nowrap mx-4"
				variant="contained"
				color="secondary"
				onClick={() => {
					setExperimentSearch('');
					setShowDialog(true);
				}}
				startIcon={<BsClockHistory className="hidden sm:flex" />}
			>
				Experiments
			</Button>
			<ThemeProvider theme={mainTheme}>
				<Dialog open={showDialog} maxWidth="lg" fullWidth>
					<DialogTitle disableTypography className={"border-b flex " + (viewMode === 'COMPARE' ? 'pb-0' : '')}>

						<div className='flex items-center justify-center'>What If Experiments</div>

						<div className='flex-1 flex items-center justify-end'>

							{viewMode !== 'COMPARE' && canExport && <Button component="a"
								href={`${nBossConnection.basePath}whatIfExperimentEditControl/export?whatIfId=${id}&aspectId=${currentAspectId}`}
								target="_blank"
								rel="noopener noreferrer"
								variant="contained"
								color="secondary">
								<ExitToAppIcon className='mr-8' fontSize="small" />Export
							</Button>}

							{viewMode === 'COMPARE' && (<Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} aria-label="compare-properties-tabs">
								<Tab label="Result" />
								<Tab label="Label styles" />
							</Tabs>)}

						</div>

					</DialogTitle>
					<DialogContent className="p-20">
						<FormProvider {...methods}>
							{loading && <FuseLoading />}
							{!loading && viewMode === 'COMPARE' && tabValue === 0 && <ExperimentCompare experimentList={selected.map(selectedId => experimentList.find(e => e.id === selectedId))} style={styleRef.current} />}
							{!loading && viewMode === 'COMPARE' && tabValue === 1 && (
								<CompareStyle
									value={{ ...defaultStyle }}
									t={t}
									onChange={value => {
										styleRef.current = value;
									}}
								/>
							)}
							{!loading && viewMode === 'LIST' && (
								<div className="flex flex-col">
									<TextField
										fullWidth
										label="Search Experiments"
										variant="outlined"
										value={experimentSearch}
										onChange={event => setExperimentSearch(event.target.value)}
										className="mb-16"
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<SearchIcon fontSize="small" />
												</InputAdornment>
											)
										}}
									/>
									<TableContainer component={Paper}>
									<Table className="w-full" aria-label="simple table">
										<TableHead>
											<TableRow>
												<TableCell padding="checkbox">
													<Checkbox
														indeterminate={indeterminate}
														checked={selected.length > 0 && selected.length === experimentList.length}
														onChange={() => handleSelectAllClick()}
														disabled={experimentList.length === 0}
													/>
												</TableCell>
												<TableCell>Name</TableCell>
												<TableCell>Creator</TableCell>
												<TableCell align="right">Actions</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{filteredExperimentList.length > 0 ? (
												filteredExperimentList.map(row => (
													<TableRow key={row.name}>
														<TableCell padding="checkbox">
															<Checkbox checked={selected.indexOf(row.id) > -1} onChange={() => handleSelectedChange(row.id)} />
														</TableCell>
														<TableCell component="th" scope="row">
															{row.name}
														</TableCell>
														<TableCell>{row.creator ? row.creator.name : ''}</TableCell>
														<TableCell className="font-mono" align="right">
															<Tooltip title="Copy Link for Experiment">
																<span>
																	<IconButton aria-label="load" onClick={e => copyExperimentLink(row)}>
																		<LinkIcon fontSize="small" />
																	</IconButton>
																</span>
															</Tooltip>
															<Tooltip title="Load experiment">
																<span>
																	<IconButton aria-label="load" onClick={e => loadExperiment(row)}>
																		<LoadIcon fontSize="small" />
																	</IconButton>
																</span>
															</Tooltip>
															{/* <Tooltip title="Export experiment">
																<span>
																	<IconButton disabled={!row.aspect || !row.whatIf} aria-label="export" onClick={e => window.open(`${nBossConnection.basePath}whatIfExperimentEditControl/export?whatIfId=${row.whatIf.id}&aspectId=${row.aspect.id}`)}>
																		<ExitToAppIcon fontSize="small" />
																	</IconButton>
																</span>
															</Tooltip> */}
															<Tooltip title={t('tableBrowser:DELETE')}>
																<span>
																	<IconButton
																		aria-label="delete"
																		onClick={e => {
																			setShowDeleteConfirm(row.id);
																			e.preventDefault();
																		}}
																	>
																		<DeleteIcon fontSize="small" />
																	</IconButton>
																</span>
															</Tooltip>

														</TableCell>
													</TableRow>
												))
											) : (
												<TableRow>
													<TableCell component="th" scope="row" colSpan={4} align="center">
														{experimentList.length > 0 ? 'No matching experiments' : 'No experiments available'}
													</TableCell>
												</TableRow>
											)}
											<ConfirmDialog
												show={showDeleteConfirm}
												title={t('tableBrowser:DELETE')}
												description={t('tableBrowser:DELETE_DESC')}
												onYes={() => handleDelete()}
												onClose={() => setShowDeleteConfirm(null)}
											/>
										</TableBody>
									</Table>
									</TableContainer>
								</div>
							)}
							{!loading && viewMode === 'SAVE' && (
								<Controller
									control={methods.control}
									name="name"
									render={({ field, fieldState }) => (
										<EditFormTextField
											field={field}
											fieldState={fieldState}
											config={{ topic: 'whatIf' }}
											fieldConfig={{
												key: 'name',
												text: t('name'),
												required: true
											}}
										/>
									)}
								/>
							)}
						</FormProvider>
					</DialogContent>
					<DialogActions className="justify-between border-t p-20">
						{(viewMode === 'LIST' || viewMode === 'COMPARE') && (
							<Button
								onClick={() => {
									if (viewMode === 'COMPARE') setViewMode('LIST');
									else setShowDialog(false);
								}}
								disabled={loading}
							>
								Close
							</Button>
						)}
						{viewMode === 'SAVE' && (
							<Button
								onClick={() => {
									setViewMode('LIST');
								}}
								disabled={loading}
							>
								Cancel
							</Button>
						)}
						{viewMode === 'SAVE' && (
							<Button onClick={methods.handleSubmit(handleSave)} disabled={loading}>
								Save
							</Button>
						)}
						{viewMode === 'LIST' && <CompareViewComponent btnType="simple" />}
						{viewMode === 'LIST' && (
							<Button onClick={() => setViewMode('COMPARE')} disabled={selected.length < 2}>
								Compare
							</Button>
						)}
						{viewMode === 'LIST' && (
							<Button onClick={() => setCompareOnScreen()} disabled={selected.length !== 1}>
								Compare on screen
							</Button>
						)}
						{viewMode === 'LIST' && (
							<Button onClick={() => setViewMode('SAVE')} disabled={loading || !canSaveExperiment}>
								Save new experiment
							</Button>
						)}
					</DialogActions>
				</Dialog>
			</ThemeProvider>
		</>
	);
});

function CompareStyle(props) {
	const { t, value, onChange } = props;
	const methods = useForm({
		defaultValues: value,
		mode: 'onChange'
	});
	const watchAllFields = methods.watch();

	useEffect(() => {
		onChange(methods.getValues());
	}, [watchAllFields]);

	return (
		<FormProvider {...methods}>
			<ValueStyleConfigFields prefix="variableStyle" title="Variable" t={t} methods={methods} />
			<ValueStyleConfigFields prefix="componentStyle" title="Component" t={t} methods={methods} />
		</FormProvider>
	);
}
